import React, {
  ReactEventHandler,
  useEffect,
  Children,
  useRef,
  createRef,
  useState,
  useCallback,
} from 'react';
import styled, { css } from 'styled-components';

const activeStyle = css`
  border: 1px solid var(--primaryColor);
  color: var(--primaryColor);

  svg {
    path {
      fill: var(--primaryColor);
    }
  }
`;

const Wrapper = styled.div`
  width: 100%;
  display: grid;
  gap: 6px;

  grid-template-columns: repeat(4, 1fr);

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const sizeMap = {
  lg: css``,
  md: css`
    height: 40px;
    padding: 0 8px;
  `,
  sm: css`
    height: 32px;
    padding: 0 8px;
  `,
};

const Chip = styled.div<{ isActive: boolean; size: 'lg' | 'md' | 'sm' }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 6px;
  border-radius: var(--cardBorderRadius);
  border: 1px solid var(--lineColor);
  background-color: var(--cardBackgroundColor);

  transition: 0.2s;

  &:hover {
    cursor: var(--hoverCursorURL), pointer;
  }

  ${({ isActive }) => isActive && activeStyle}
  ${({ size }) => size && sizeMap[size]}
`;

interface RadioChipProps {
  children?: any | undefined;
  setKey: (childKey) => void;
  defaultKey?: string;
  size?: 'lg' | 'md' | 'sm';
  style?: React.CSSProperties;
}

function RadioChips({
  children,
  setKey,
  defaultKey,
  size = 'md',
  style,
}: RadioChipProps) {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [hasUserInteraction, setHasUserInteraction] = useState<boolean>(false); // 🔧 사용자 상호작용 추적


  const renderedChildren = Children.toArray(children).filter(
    (child): child is React.ReactElement =>
      !!child &&
      typeof child === 'object' &&
      'props' in child &&
      !child.props?.hidden,
  );

  // 🔧 renderedChildren 길이에 맞춰 chipRefs 생성
  const chipRefs: any = useRef(
    Array.from({ length: renderedChildren.length }, () => createRef()),
  );



  const handleClick = useCallback(
    (clickedIndex: number, childKey: string | undefined) => {

      setCurrentIndex(clickedIndex);
      setHasUserInteraction(true); // 🔧 사용자가 직접 선택했음을 표시
      if (childKey) setKey(childKey);
    },
    [setKey], // 🔧 currentIndex 제거 - 불필요한 리렌더링 방지
  );

  // defaultKey 기준 index 설정 (초기 마운트 시에만)
  useEffect(() => {
    if (!defaultKey || hasUserInteraction) return; // 🔧 사용자 상호작용 후에는 defaultKey 무시


    for (let i = 0; i < renderedChildren.length; i++) {
      const key = renderedChildren[i].key;
      const cleanKey = key?.toString().replace(/^\.\$/, '');

      if (cleanKey === defaultKey) {
        setCurrentIndex(i);
        break;
      }
    }
  }, [defaultKey, hasUserInteraction]); // 🔧 hasUserInteraction도 dependency에 추가

  return (
    <Wrapper
      style={{
        gridTemplateColumns: `repeat(${renderedChildren.length},1fr)`,
        ...style,
      }}
    >
      {renderedChildren.map((child, index) => {
        // child.key  === children의 props 중, key값 받아옴
        if (child.props.hidden) return null;

        const isActive = currentIndex === index;
        const childKey = child.key?.toString().replace(/^\.\$/, '') || '';

        return (
          <Chip
            key={index}
            ref={chipRefs.current[index]}
            onClick={() => {
              handleClick(index, childKey);
            }}
            isActive={isActive}
            size={size}
          >
            {child}
          </Chip>
        );
      })}
    </Wrapper>
  );
}

export default RadioChips;
